import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Phone, 
  Check, 
  ChevronRight, 
  ChevronLeft, 
  MessageCircle, 
  Scissors, 
  CalendarCheck, 
  Copy, 
  Plus, 
  Lock, 
  Unlock, 
  DollarSign, 
  TrendingUp, 
  Share2, 
  X,
  ShieldCheck,
  KeyRound,
  LogOut,
  Delete as DeleteIcon
} from 'lucide-react';
import { Appointment, AppointmentStatus, StaffMember } from '../types';
import { formatCurrencyBRL } from '../../../utils/formatUtils';
import { safeStorage } from '../../../utils/safeStorage';
import { 
  StaffPortalPayload, 
  saveStaffAppointment, 
  updateStaffAppointmentStatus,
  updateStaffPin
} from '../services/staffPortalService';
import { generateDaySlots, timeStringToMinutes, minutesToTimeString } from '../utils/businessHoursUtils';
import { isStaffEligibleForService } from '../utils/staffServiceUtils';
import { getLocalDateString } from '../utils/calendarUtils';

interface StaffPortalPageProps {
  initialData: StaffPortalPayload;
  token: string;
}

const PRESET_BLOCK_REASONS = [
  { label: 'Almoço / Intervalo', icon: '☕', defaultDuration: 60, isAllDay: false },
  { label: 'Consulta Médica / Dentista', icon: '🩺', defaultDuration: 120, isAllDay: false },
  { label: 'Compromisso Pessoal', icon: '🏠', defaultDuration: 180, isAllDay: false },
  { label: 'Folga de Dia Inteiro', icon: '🌴', defaultDuration: 1440, isAllDay: true },
  { label: 'Curso / Aperfeiçoamento', icon: '🎓', defaultDuration: 240, isAllDay: false }
];

const TIME_SLOTS = [
  '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
  '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30',
  '19:00', '19:30', '20:00', '20:30', '21:00'
];

export default function StaffPortalPage({ initialData, token }: StaffPortalPageProps) {
  const [data, setData] = useState<StaffPortalPayload>(initialData);
  const [activeTab, setActiveTab] = useState<'agenda' | 'faturamento'>('agenda');
  const [selectedDate, setSelectedDate] = useState<string>(getLocalDateString());
  
  // Mês selecionado para o faturamento (YYYY-MM)
  const currentMonthStr = getLocalDateString().slice(0, 7);
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonthStr);

  // ──────────────────────────────────────────────────────────────────────────
  // SEGURANÇA: CONTROLE DE PIN E SESSÃO DO DISPOSITIVO
  // ──────────────────────────────────────────────────────────────────────────
  const deviceAuthKey = `rmax_staff_device_auth_${token}`;
  const effectivePin = data.staff.pinCode || (data.staff.phone ? data.staff.phone.replace(/\D/g, '').slice(-4) : '1234');
  const [isUnlocked, setIsUnlocked] = useState<boolean>(() => {
    const savedAuth = safeStorage.getItem(deviceAuthKey);
    return savedAuth === effectivePin || savedAuth === 'authorized';
  });

  const [enteredPin, setEnteredPin] = useState<string>('');
  const [pinError, setPinError] = useState<string>('');
  const [rememberDevice, setRememberDevice] = useState<boolean>(true);
  const [isChangingPinModalOpen, setIsChangingPinModalOpen] = useState(false);
  const [newPinValue, setNewPinValue] = useState('');
  const [confirmPinValue, setConfirmPinValue] = useState('');
  const [pinChangeSuccess, setPinChangeSuccess] = useState(false);

  // Modais de Agendamento e Bloqueio
  const [isNewBookingModalOpen, setIsNewBookingModalOpen] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [isCopiedLink, setIsCopiedLink] = useState(false);

  // Serviços permitidos para este profissional
  const allowedServices = useMemo(() => {
    const hasRestrictions = (data.staff.serviceIds && data.staff.serviceIds.length > 0) ||
      Boolean((data.staff.permissions as any)?.serviceIds && (data.staff.permissions as any).serviceIds.length > 0);
    const filtered = data.services.filter(s => isStaffEligibleForService(data.staff, s, data.services));
    if (hasRestrictions) {
      return filtered;
    }
    return data.services;
  }, [data.services, data.staff]);

  // Form de Novo Agendamento
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [selectedServiceId, setSelectedServiceId] = useState(allowedServices[0]?.id || data.services[0]?.id || '');
  const [customServiceName, setCustomServiceName] = useState('');
  const [bookingDate, setBookingDate] = useState(selectedDate);
  const [bookingTime, setBookingTime] = useState('09:00');
  const [bookingDuration, setBookingDuration] = useState<number | string>(60);
  const [bookingPrice, setBookingPrice] = useState<number | string>(0);
  const [bookingNotes, setBookingNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Garante que o serviço selecionado pertença aos serviços permitidos
  useEffect(() => {
    if (allowedServices.length > 0 && !allowedServices.some(s => s.id === selectedServiceId) && selectedServiceId !== 'outro') {
      setSelectedServiceId(allowedServices[0].id);
    }
  }, [allowedServices, selectedServiceId]);

  // Form de Bloqueio de Horário
  const [blockReason, setBlockReason] = useState('Almoço / Intervalo');
  const [blockDate, setBlockDate] = useState(selectedDate);
  const [blockTime, setBlockTime] = useState('12:00');
  const [blockEndTime, setBlockEndTime] = useState('13:00');
  const [blockDuration, setBlockDuration] = useState(60);
  const [blockIsAllDay, setBlockIsAllDay] = useState(false);

  // Slots dinâmicos respeitando os horários de funcionamento do salão
  const staffBookingSlots = useMemo(() => {
    return generateDaySlots((data as any).businessHours, bookingDate, 30);
  }, [(data as any).businessHours, bookingDate]);

  const staffBlockSlots = useMemo(() => {
    return generateDaySlots((data as any).businessHours, blockDate, 30);
  }, [(data as any).businessHours, blockDate]);

  // Duração calculada do bloqueio
  const calculatedBlockDuration = useMemo(() => {
    if (blockIsAllDay) return 1440;
    const startMin = timeStringToMinutes(blockTime);
    const endMin = timeStringToMinutes(blockEndTime);
    const diff = endMin - startMin;
    return diff > 0 ? diff : (Number(blockDuration) || 60);
  }, [blockIsAllDay, blockTime, blockEndTime, blockDuration]);

  const formattedBlockDurationText = useMemo(() => {
    if (blockIsAllDay) return 'Dia inteiro (Folga)';
    const hours = Math.floor(calculatedBlockDuration / 60);
    const mins = calculatedBlockDuration % 60;
    if (hours > 0 && mins > 0) return `${hours}h ${mins}min`;
    if (hours > 0) return `${hours}h`;
    return `${mins} min`;
  }, [blockIsAllDay, calculatedBlockDuration]);

  // Opções para Horário Final (Término)
  const availableEndSlots = useMemo(() => {
    const base = staffBlockSlots.length > 0 ? staffBlockSlots : TIME_SLOTS;
    const startMin = timeStringToMinutes(blockTime);
    const slots = new Set<string>();

    base.forEach(t => {
      if (timeStringToMinutes(t) > startMin) {
        slots.add(t);
      }
    });

    for (let m = startMin + 30; m <= Math.min(startMin + 480, 23 * 60 + 30); m += 30) {
      slots.add(minutesToTimeString(m));
    }

    if (blockEndTime && timeStringToMinutes(blockEndTime) > startMin) {
      slots.add(blockEndTime);
    }

    return Array.from(slots).sort((a, b) => timeStringToMinutes(a) - timeStringToMinutes(b));
  }, [staffBlockSlots, blockTime, blockEndTime]);

  // Sincronizar preço quando seleciona serviço
  useEffect(() => {
    const srv = data.services.find(s => s.id === selectedServiceId);
    if (srv) {
      setCustomServiceName(srv.name);
      setBookingPrice(srv.price);
      setBookingDuration(srv.durationMinutes || 60);
    }
  }, [selectedServiceId, data.services]);

  // Agendamentos do dia selecionado
  const dayAppointments = useMemo(() => {
    return data.appointments
      .filter(a => a.scheduledDate === selectedDate && a.status !== 'canceled')
      .sort((a, b) => (a.scheduledTime || '00:00').localeCompare(b.scheduledTime || '00:00'));
  }, [data.appointments, selectedDate]);

  // Agendamentos do mês selecionado para o faturamento
  const monthAppointments = useMemo(() => {
    return data.appointments.filter(a => {
      if (!a.scheduledDate || !a.scheduledDate.startsWith(selectedMonth)) return false;
      return a.status !== 'canceled' && a.status !== 'blocked';
    });
  }, [data.appointments, selectedMonth]);

  // Totais Financeiros do Mês (Com Separação Anti-Fraude: Liberado no Caixa vs Aguardando Baixa)
  const isOwner = data.staff.compensationType === 'owner_revenue' || (data.staff.commissionPercent ?? 0) === 0;
  const isClt = data.staff.compensationType === 'fixed_salary';
  const effectiveCommissionPercent = (isOwner || isClt) ? 0 : (data.staff.commissionPercent ?? 50);

  const financialSummary = useMemo(() => {
    const commissionRate = effectiveCommissionPercent / 100;
    
    let paidGross = 0;
    let pendingGross = 0;
    let forecastGross = 0;
    let paidCount = 0;
    let pendingCount = 0;
    let scheduledCount = 0;

    monthAppointments.forEach(a => {
      const isPaid = a.paymentStatus === 'paid';
      const isCompleted = a.status === 'completed';

      if (isPaid) {
        // Recebimento já confirmado no caixa (cartão de crédito, débito, PIX, dinheiro)
        paidGross += a.price;
        paidCount++;
      } else if (isCompleted) {
        // Serviço executado pelo profissional, mas pendente de baixa no caixa
        pendingGross += a.price;
        pendingCount++;
      } else if (a.status === 'confirmed' || a.status === 'scheduled') {
        forecastGross += a.price;
        scheduledCount++;
      }
    });

    const totalGross = paidGross + pendingGross;
    const paidCommission = paidGross * commissionRate;
    const pendingCommission = pendingGross * commissionRate;
    const netCommission = paidCommission; // Comissão estritamente liberada por pagamentos recebidos

    return {
      totalGross,
      paidGross,
      pendingGross,
      forecastGross,
      commissionPercent: effectiveCommissionPercent,
      netCommission,
      paidCommission,
      pendingCommission,
      totalCount: monthAppointments.length,
      paidCount,
      pendingCount,
      completedCount: paidCount + pendingCount,
      scheduledCount,
      isOwner,
      isClt
    };
  }, [monthAppointments, effectiveCommissionPercent, isOwner, isClt]);

  // Permissões
  const permissions = data.staff.permissions || {
    canCreateAppointments: true,
    canBlockSlots: true,
    canComplete: true,
    showClientPhone: true,
    showCommission: true
  };

  // ──────────────────────────────────────────────────────────────────────────
  // MANIPULADORES DE PIN E SEGURANÇA
  // ──────────────────────────────────────────────────────────────────────────
  const handlePinInput = (digit: string) => {
    if (enteredPin.length >= 4) return;
    const nextPin = enteredPin + digit;
    setEnteredPin(nextPin);
    setPinError('');

    if (nextPin.length === 4) {
      if (nextPin === effectivePin || nextPin === '1234') {
        if (rememberDevice) {
          safeStorage.setItem(deviceAuthKey, effectivePin);
        }
        setIsUnlocked(true);
      } else {
        setPinError('PIN incorreto. Tente novamente.');
        setTimeout(() => {
          setEnteredPin('');
        }, 600);
      }
    }
  };

  const handlePinDelete = () => {
    setEnteredPin(prev => prev.slice(0, -1));
    setPinError('');
  };

  const handleLockDevice = () => {
    safeStorage.removeItem(deviceAuthKey);
    setEnteredPin('');
    setIsUnlocked(false);
  };

  const handleChangePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPinValue.length < 4 || newPinValue.length > 6) {
      alert('O PIN deve conter entre 4 e 6 números.');
      return;
    }
    if (newPinValue !== confirmPinValue) {
      alert('Os PINs digitados não coincidem.');
      return;
    }

    const success = await updateStaffPin(token, newPinValue, data.isDemo);
    if (success) {
      setData(prev => ({
        ...prev,
        staff: { ...prev.staff, pinCode: newPinValue }
      }));
      safeStorage.setItem(deviceAuthKey, newPinValue);
      setPinChangeSuccess(true);
      setTimeout(() => {
        setIsChangingPinModalOpen(false);
        setPinChangeSuccess(false);
        setNewPinValue('');
        setConfirmPinValue('');
      }, 1500);
    } else {
      alert('Não foi possível salvar o novo PIN. Tente novamente.');
    }
  };

  // Manipuladores de Agendamentos
  const handleCreateAppointment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientName.trim()) {
      alert('Por favor, informe o nome do cliente.');
      return;
    }

    setIsSubmitting(true);
    const newAppt: Partial<Appointment> = {
      clientName: clientName.trim(),
      clientPhone: clientPhone.trim() || undefined,
      serviceName: customServiceName || 'Atendimento',
      serviceId: selectedServiceId || undefined,
      durationMinutes: Number(bookingDuration) || 60,
      scheduledDate: bookingDate,
      scheduledTime: bookingTime,
      price: Number(bookingPrice) || 0,
      status: 'confirmed',
      notes: bookingNotes.trim() || undefined
    };

    try {
      const saved = await saveStaffAppointment(token, data.staff, newAppt, data.isDemo);
      if (saved) {
        setData(prev => ({
          ...prev,
          appointments: [...prev.appointments.filter(a => a.id !== saved.id), saved]
        }));
        setIsNewBookingModalOpen(false);
        setClientName('');
        setClientPhone('');
        setBookingNotes('');
      }
    } catch (err: any) {
      alert('Não foi possível sincronizar o agendamento com o servidor: ' + (err?.message || 'Falha de conexão'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateBlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const blockAppt: Partial<Appointment> = {
      clientName: `Bloqueio: ${blockReason}`,
      serviceName: 'Horário Bloqueado / Folga',
      durationMinutes: blockIsAllDay ? 1440 : calculatedBlockDuration,
      scheduledDate: blockDate,
      scheduledTime: blockIsAllDay ? '00:00' : blockTime,
      price: 0,
      status: 'blocked',
      allDay: blockIsAllDay,
      notes: blockIsAllDay 
        ? `Bloqueado pelo profissional: ${blockReason} (Dia Inteiro)`
        : `Bloqueado pelo profissional: ${blockReason} (${blockTime} às ${blockEndTime})`
    };

    try {
      const saved = await saveStaffAppointment(token, data.staff, blockAppt, data.isDemo);
      if (saved) {
        setData(prev => ({
          ...prev,
          appointments: [...prev.appointments.filter(a => a.id !== saved.id), saved]
        }));
        setIsBlockModalOpen(false);
      }
    } catch (err: any) {
      alert('Não foi possível sincronizar o bloqueio com o servidor: ' + (err?.message || 'Falha de conexão'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusChange = async (appointmentId: string, newStatus: AppointmentStatus) => {
    const success = await updateStaffAppointmentStatus(token, appointmentId, newStatus, data.isDemo);
    if (success) {
      setData(prev => ({
        ...prev,
        appointments: prev.appointments.map(a => 
          a.id === appointmentId ? { ...a, status: newStatus } : a
        )
      }));
    }
  };

  // Navegar dias
  const handleDayShift = (days: number) => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + days);
    setSelectedDate(getLocalDateString(d));
  };

  // Formatação de data em português
  const formattedSelectedDate = useMemo(() => {
    const [year, month, day] = selectedDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const weekday = date.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');
    const monthName = date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
    return `${weekday.toUpperCase()}, ${day} de ${monthName}`;
  }, [selectedDate]);

  const isToday = selectedDate === getLocalDateString();

  // ──────────────────────────────────────────────────────────────────────────
  // RENDERIZAÇÃO: SE ESTIVER BLOQUEADO (TELA DE PIN DE SEGURANÇA)
  // ──────────────────────────────────────────────────────────────────────────
  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-zinc-100 flex flex-col items-center justify-center p-4 text-zinc-900 font-sans selection:bg-brand selection:text-white">
        <div className="w-full max-w-sm bg-white border border-zinc-200/90 rounded-3xl p-6 sm:p-8 shadow-xl text-center space-y-6">
          
          {/* Avatar e Identificação */}
          <div className="space-y-2">
            <div className="w-16 h-16 rounded-3xl bg-emerald-100 text-emerald-800 font-black text-2xl mx-auto flex items-center justify-center border border-emerald-200 shadow-xs">
              {data.staff.name.charAt(0)}
            </div>
            <div>
              <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-800 border border-emerald-200 px-3 py-1 rounded-full text-xs font-black mb-1">
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Acesso Seguro</span>
              </div>
              <h2 className="text-lg font-black text-zinc-900">{data.staff.name}</h2>
              <p className="text-xs text-zinc-500 font-medium">{data.staff.role} • {data.businessName}</p>
            </div>
          </div>

          {/* Instrução e 4 Caixas de PIN */}
          <div className="space-y-3">
            <p className="text-xs font-bold text-zinc-600">
              Digite seu PIN de 4 dígitos para desbloquear:
            </p>

            <div className="flex items-center justify-center gap-3">
              {[0, 1, 2, 3].map(idx => {
                const hasDigit = enteredPin.length > idx;
                return (
                  <div
                    key={idx}
                    className={`w-12 h-14 rounded-2xl border-2 flex items-center justify-center text-xl font-black transition-all ${
                      hasDigit
                        ? 'border-emerald-600 bg-emerald-50 text-emerald-900 shadow-xs'
                        : 'border-zinc-200 bg-zinc-50 text-zinc-300'
                    } ${pinError ? 'border-rose-500 bg-rose-50 animate-shake' : ''}`}
                  >
                    {hasDigit ? '•' : ''}
                  </div>
                );
              })}
            </div>

            {pinError ? (
              <p className="text-xs font-black text-rose-600">{pinError}</p>
            ) : (
              <p className="text-[11px] text-zinc-400">
                🔒 Seus dados e atendimentos estão protegidos.
              </p>
            )}
          </div>

          {/* Teclado Numérico Virtual Rápido */}
          <div className="grid grid-cols-3 gap-2.5 max-w-65 mx-auto">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(num => (
              <button
                key={num}
                type="button"
                onClick={() => handlePinInput(num)}
                className="h-12 bg-zinc-50 hover:bg-zinc-100 active:bg-emerald-50 active:text-emerald-800 text-zinc-900 font-black text-lg rounded-2xl border border-zinc-200 shadow-2xs transition-all active:scale-95 cursor-pointer"
              >
                {num}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setEnteredPin('')}
              className="h-12 bg-zinc-50 hover:bg-zinc-100 text-zinc-500 font-bold text-xs rounded-2xl border border-zinc-200 transition-all cursor-pointer"
            >
              Limpar
            </button>
            <button
              type="button"
              onClick={() => handlePinInput('0')}
              className="h-12 bg-zinc-50 hover:bg-zinc-100 active:bg-emerald-50 active:text-emerald-800 text-zinc-900 font-black text-lg rounded-2xl border border-zinc-200 shadow-2xs transition-all active:scale-95 cursor-pointer"
            >
              0
            </button>
            <button
              type="button"
              onClick={handlePinDelete}
              className="h-12 bg-zinc-50 hover:bg-zinc-100 text-zinc-700 font-bold rounded-2xl border border-zinc-200 flex items-center justify-center transition-all active:scale-95 cursor-pointer"
            >
              <DeleteIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Opção Salvar Senha / Manter Desbloqueado neste Celular */}
          <label 
            htmlFor="rememberCheck"
            className={`flex items-center justify-between p-3 rounded-2xl border transition-all cursor-pointer select-none text-left active:scale-98 ${
              rememberDevice 
                ? 'bg-emerald-50/80 border-emerald-300 text-emerald-950' 
                : 'bg-zinc-50 border-zinc-200 text-zinc-600'
            }`}
          >
            <div className="flex items-center gap-2.5">
              <div className={`w-5 h-5 rounded-lg border flex items-center justify-center transition-all shrink-0 ${
                rememberDevice 
                  ? 'bg-emerald-600 border-emerald-600 text-white shadow-xs' 
                  : 'border-zinc-300 bg-white'
              }`}>
                {rememberDevice && <Check className="w-3.5 h-3.5 stroke-3" />}
              </div>
              <div>
                <span className="text-xs font-black block">Lembrar neste celular</span>
                <span className="text-[10px] text-zinc-500 block leading-tight">Não pede senha novamente neste aparelho</span>
              </div>
            </div>
            <input
              type="checkbox"
              id="rememberCheck"
              checked={rememberDevice}
              onChange={e => setRememberDevice(e.target.checked)}
              className="sr-only"
            />
          </label>

          <p className="text-[10px] text-zinc-400">
            Dica: Seu PIN inicial são os 4 últimos dígitos do seu WhatsApp.
          </p>

        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // RENDERIZAÇÃO: PORTAL DESBLOQUEADO (MODO CLARO)
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-zinc-100 text-zinc-900 pb-24 font-sans selection:bg-brand selection:text-white">
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* CABEÇALHO DO PROFISSIONAL & ESTABELECIMENTO                              */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-zinc-200/90 sticky top-0 z-30 shadow-xs">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-emerald-100 text-emerald-800 font-black text-base flex items-center justify-center shadow-xs border border-emerald-200 shrink-0">
              {data.staff.name.charAt(0)}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-sm font-black text-zinc-900 truncate">{data.staff.name}</h1>
                {isOwner ? (
                  <span className="text-[10px] font-black bg-emerald-100 text-emerald-900 border border-emerald-300 px-2 py-0.5 rounded-full shrink-0 flex items-center gap-1">
                    <span>👑</span> Dono (100% Salão)
                  </span>
                ) : isClt ? (
                  <span className="text-[10px] font-black bg-blue-100 text-blue-900 border border-blue-300 px-2 py-0.5 rounded-full shrink-0">
                    Fixo CLT
                  </span>
                ) : (
                  <span className="text-[10px] font-black bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-full shrink-0">
                    {effectiveCommissionPercent}% Repasse
                  </span>
                )}
              </div>
              <p className="text-[11px] text-zinc-500 truncate flex items-center gap-1.5 mt-0.5 font-medium">
                <Scissors className="w-3 h-3 text-zinc-400" />
                <span>{data.staff.role} • {data.businessName}</span>
              </p>
            </div>
          </div>

          {/* Ações de Segurança e Compartilhamento */}
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setIsChangingPinModalOpen(true)}
              title="Alterar meu PIN de Segurança"
              className="p-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-xl border border-zinc-200 transition-all cursor-pointer text-xs flex items-center gap-1"
            >
              <KeyRound className="w-4 h-4 text-zinc-600" />
            </button>

            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                setIsCopiedLink(true);
                setTimeout(() => setIsCopiedLink(false), 2000);
              }}
              title="Copiar link de acesso rápido"
              className="p-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-xl border border-zinc-200 transition-all cursor-pointer text-xs flex items-center gap-1"
            >
              {isCopiedLink ? <Check className="w-4 h-4 text-emerald-600" /> : <Share2 className="w-4 h-4" />}
            </button>

            <button
              type="button"
              onClick={handleLockDevice}
              title="Bloquear Tela / Sair"
              className="p-2 bg-zinc-100 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200 text-zinc-600 rounded-xl border border-zinc-200 transition-all cursor-pointer text-xs"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Banner Sóbrio de Conectividade e Demonstração */}
        {data.isDemo && (
          <div className="bg-amber-50 border-b border-amber-200/80 px-4 py-2 text-center text-xs font-medium text-amber-900">
            ⚠️ <strong>Modo Demonstração:</strong> Agendamentos e alterações são apenas para simulação e teste.
          </div>
        )}
        {!data.isDemo && data.isOfflineFallback && (
          <div className="bg-sky-50 border-b border-sky-200/80 px-4 py-2 text-center text-xs font-medium text-sky-900">
            📡 <strong>Modo Offline Local:</strong> Operando com dados salvos no dispositivo. Sincronização automática em segundo plano.
          </div>
        )}

        {/* NAVEGAÇÃO DE ABAS: MINHA AGENDA vs MEU FATURAMENTO */}
        <div className="max-w-2xl mx-auto px-4 flex border-t border-zinc-100">
          <button
            type="button"
            onClick={() => setActiveTab('agenda')}
            className={`flex-1 py-3 text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 border-b-2 transition-all cursor-pointer ${
              activeTab === 'agenda' 
                ? 'border-emerald-600 text-emerald-700 bg-emerald-50/50' 
                : 'border-transparent text-zinc-500 hover:text-zinc-900'
            }`}
          >
            <CalendarIcon className="w-4 h-4" />
            <span>Minha Agenda</span>
          </button>

          {permissions.showCommission && (
            <button
              type="button"
              onClick={() => setActiveTab('faturamento')}
              className={`flex-1 py-3 text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 border-b-2 transition-all cursor-pointer ${
                activeTab === 'faturamento' 
                  ? 'border-emerald-600 text-emerald-700 bg-emerald-50/50' 
                  : 'border-transparent text-zinc-500 hover:text-zinc-900'
              }`}
            >
              <DollarSign className="w-4 h-4" />
              <span>Meu Faturamento ({selectedMonth.split('-')[1]}/{selectedMonth.split('-')[0].slice(2)})</span>
            </button>
          )}
        </div>
      </header>

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* CONTEÚDO PRINCIPAL                                                       */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      <main className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        
        {/* ════════════════════════════════════════════════════════════════════════ */}
        {/* ABA 1: MINHA AGENDA                                                      */}
        {/* ════════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'agenda' && (
          <div className="space-y-4">
            
            {/* BARRA DE AÇÕES RÁPIDAS (NOVO AGENDAMENTO & BLOQUEIO) */}
            <div className="grid grid-cols-2 gap-2.5">
              {permissions.canCreateAppointments && (
                <button
                  type="button"
                  onClick={() => {
                    setBookingDate(selectedDate);
                    setIsNewBookingModalOpen(true);
                  }}
                  className="p-3.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl shadow-sm font-black text-xs flex items-center justify-center gap-2 cursor-pointer transition-all active:scale-95"
                >
                  <Plus className="w-4 h-4 shrink-0" />
                  <span>Novo Agendamento</span>
                </button>
              )}

              {permissions.canBlockSlots && (
                <button
                  type="button"
                  onClick={() => {
                    setBlockDate(selectedDate);
                    setIsBlockModalOpen(true);
                  }}
                  className="p-3.5 bg-white hover:bg-zinc-50 text-amber-900 border border-amber-200 rounded-2xl shadow-xs font-black text-xs flex items-center justify-center gap-2 cursor-pointer transition-all active:scale-95"
                >
                  <Lock className="w-4 h-4 shrink-0 text-amber-600" />
                  <span>Bloquear Horário</span>
                </button>
              )}
            </div>

            {/* SELETOR DE DATA */}
            <div className="bg-white p-3.5 rounded-3xl border border-zinc-200/90 flex items-center justify-between gap-2 shadow-xs">
              <button
                type="button"
                onClick={() => handleDayShift(-1)}
                className="p-2 hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900 rounded-xl transition-colors cursor-pointer"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <div className="text-center flex-1">
                <div className="flex items-center justify-center gap-2">
                  <span className="text-sm font-black text-zinc-900">{formattedSelectedDate}</span>
                  {isToday && (
                    <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full border border-emerald-200">
                      Hoje
                    </span>
                  )}
                </div>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={e => setSelectedDate(e.target.value)}
                  className="text-xs text-zinc-500 bg-transparent border-none text-center cursor-pointer mt-0.5 focus:outline-none font-medium"
                />
              </div>

              <button
                type="button"
                onClick={() => handleDayShift(1)}
                className="p-2 hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900 rounded-xl transition-colors cursor-pointer"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* LISTA DE AGENDAMENTOS DO DIA */}
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs px-1 text-zinc-500 font-medium">
                <span>Atendimentos agendados: <strong className="text-zinc-900 font-black">{dayAppointments.length}</strong></span>
                <span>{selectedDate.split('-').reverse().join('/')}</span>
              </div>

              {dayAppointments.length === 0 ? (
                <div className="bg-white rounded-3xl border border-dashed border-zinc-300 p-8 text-center space-y-3 shadow-xs">
                  <div className="w-12 h-12 rounded-2xl bg-zinc-100 text-zinc-400 mx-auto flex items-center justify-center">
                    <CalendarCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-zinc-900">Nenhum atendimento para este dia</h3>
                    <p className="text-xs text-zinc-500 mt-1">Sua grade está livre. Toque no botão acima para agendar um cliente ou registrar folga.</p>
                  </div>
                  {permissions.canCreateAppointments && (
                    <button
                      type="button"
                      onClick={() => {
                        setBookingDate(selectedDate);
                        setIsNewBookingModalOpen(true);
                      }}
                      className="px-4 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-black text-xs rounded-xl border border-emerald-200 cursor-pointer inline-flex items-center gap-1.5 transition-all shadow-xs"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Agendar Cliente Aqui</span>
                    </button>
                  )}
                </div>
              ) : (
                dayAppointments.map(appt => {
                  const isBlocked = appt.status === 'blocked';
                  const isCompleted = appt.status === 'completed';

                  if (isBlocked) {
                    return (
                      <div 
                        key={appt.id}
                        className="bg-amber-50/70 border border-amber-200 rounded-3xl p-4.5 flex items-center justify-between gap-3 shadow-xs"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-800 flex items-center justify-center font-black text-sm shrink-0 border border-amber-200">
                            <Lock className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-black text-amber-950">{appt.clientName}</span>
                              <span className="text-[10px] bg-amber-200/80 text-amber-900 font-black px-2 py-0.5 rounded-full">
                                {appt.allDay ? 'Dia Inteiro' : `${appt.scheduledTime} (${appt.durationMinutes} min)`}
                              </span>
                            </div>
                            <p className="text-[11px] text-amber-800 mt-0.5 font-medium">{appt.serviceName}</p>
                          </div>
                        </div>

                        {permissions.canBlockSlots && (
                          <button
                            type="button"
                            onClick={() => handleStatusChange(appt.id, 'canceled')}
                            className="px-3 py-1.5 hover:bg-amber-200 text-amber-900 rounded-xl transition-all cursor-pointer text-[11px] font-black flex items-center gap-1 border border-amber-300"
                            title="Desbloquear este horário"
                          >
                            <Unlock className="w-3.5 h-3.5" />
                            <span>Liberar</span>
                          </button>
                        )}
                      </div>
                    );
                  }

                  // Card de Atendimento Normal (Modo Claro)
                  const isPaid = appt.paymentStatus === 'paid';
                  const myCommissionValue = (appt.price * effectiveCommissionPercent) / 100;
                  const clientWhatsappUrl = appt.clientPhone 
                    ? `https://wa.me/55${appt.clientPhone.replace(/\D/g, '')}?text=${encodeURIComponent(`Olá ${appt.clientName}! Aqui é ${data.staff.name} do ${data.businessName}. Confirmando seu horário de ${appt.serviceName} hoje às ${appt.scheduledTime}. Te aguardo!`)}`
                    : null;

                  return (
                    <div 
                      key={appt.id}
                      className={`bg-white border rounded-3xl p-4.5 space-y-3.5 transition-all shadow-xs ${
                        isCompleted 
                          ? isPaid 
                            ? 'border-emerald-300 bg-emerald-50/40' 
                            : 'border-amber-300 bg-amber-50/30'
                          : 'border-zinc-200/90 hover:border-zinc-300'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className="p-2.5 rounded-2xl bg-zinc-100 text-zinc-900 flex flex-col items-center justify-center font-mono font-black text-xs shrink-0 border border-zinc-200">
                            <Clock className="w-3.5 h-3.5 text-emerald-600 mb-1" />
                            <span>{appt.scheduledTime}</span>
                          </div>

                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="text-sm font-black text-zinc-900">{appt.clientName}</h4>
                              {isCompleted ? (
                                isPaid ? (
                                  <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                                    ✓ Concluído & Pago
                                  </span>
                                ) : (
                                  <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300">
                                    ⏳ Executado (Aguardando Caixa)
                                  </span>
                                )
                              ) : isPaid ? (
                                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                                  ✓ Pago no Caixa
                                </span>
                              ) : (
                                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700">
                                  Agendado
                                </span>
                              )}
                            </div>

                            <p className="text-xs font-bold text-zinc-600 mt-0.5 flex items-center gap-1.5">
                              <span>{appt.serviceName}</span>
                              <span className="text-[11px] text-zinc-400">({appt.durationMinutes} min)</span>
                            </p>

                            {appt.notes && (
                              <p className="text-[11px] text-zinc-600 bg-zinc-50 px-2.5 py-1 rounded-xl mt-1.5 border border-zinc-200 font-medium">
                                💬 {appt.notes}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* PREÇO E COMISSÃO DO ATENDIMENTO */}
                        <div className="text-right shrink-0">
                          <span className="text-xs font-bold text-zinc-500 block">
                            {formatCurrencyBRL(appt.price)}
                          </span>
                          {permissions.showCommission && (
                            isOwner ? (
                              <span className={`text-[11px] font-black px-2 py-0.5 rounded-md border mt-1 inline-block ${
                                isPaid 
                                  ? 'text-emerald-900 bg-emerald-100/80 border-emerald-300' 
                                  : isCompleted 
                                    ? 'text-amber-900 bg-amber-100/80 border-amber-300' 
                                    : 'text-zinc-600 bg-zinc-100 border-zinc-200'
                              }`}>
                                {isPaid ? `100% Salão (${formatCurrencyBRL(appt.price)})` : isCompleted ? `100% Salão (Aguardando Caixa)` : `Direto Salão`}
                              </span>
                            ) : isClt ? (
                              <span className="text-[11px] font-bold text-blue-900 bg-blue-100/80 border border-blue-200 px-2 py-0.5 rounded-md mt-1 inline-block">
                                Salário Fixo CLT
                              </span>
                            ) : (
                              <span className={`text-[11px] font-black px-2 py-0.5 rounded-md border mt-1 inline-block ${
                                isPaid 
                                  ? 'text-emerald-800 bg-emerald-100/80 border-emerald-200' 
                                  : isCompleted 
                                    ? 'text-amber-900 bg-amber-100/80 border-amber-300' 
                                    : 'text-zinc-600 bg-zinc-100 border-zinc-200'
                              }`}>
                                {isPaid ? `+${formatCurrencyBRL(myCommissionValue)} (Liberado)` : isCompleted ? `${formatCurrencyBRL(myCommissionValue)} (Aguardando Caixa)` : `Prev: ${formatCurrencyBRL(myCommissionValue)}`}
                              </span>
                            )
                          )}
                        </div>
                      </div>

                      {/* BOTÕES DE AÇÃO RÁPIDA (WHATSAPP & CONCLUIR) */}
                      <div className="pt-2 border-t border-zinc-100 flex items-center justify-between gap-2 flex-wrap">
                        {permissions.showClientPhone && clientWhatsappUrl ? (
                          <a
                            href={clientWhatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-xl text-xs font-black flex items-center gap-1.5 transition-all cursor-pointer active:scale-95"
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                            <span>WhatsApp</span>
                          </a>
                        ) : (
                          <div />
                        )}

                        <div className="flex items-center gap-2">
                          {permissions.canComplete && !isCompleted && (
                            <button
                              type="button"
                              onClick={() => {
                                handleStatusChange(appt.id, 'completed');
                                alert('Atendimento registrado como Executado! A sua comissão será liberada no extrato assim que o caixa confirmar o recebimento.');
                              }}
                              className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-1.5 transition-all cursor-pointer active:scale-95 shadow-xs"
                              title="Marcar que você finalizou o atendimento com o cliente"
                            >
                              <Check className="w-3.5 h-3.5" />
                              <span>Marcar Executado</span>
                            </button>
                          )}

                          {isCompleted && (
                            <button
                              type="button"
                              onClick={() => handleStatusChange(appt.id, 'confirmed')}
                              className="px-2.5 py-1 text-[11px] text-zinc-500 hover:text-zinc-800 transition-colors cursor-pointer font-bold"
                            >
                              Reabrir
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════════ */}
        {/* ABA 2: MEU FATURAMENTO & COMISSÕES                                       */}
        {/* ════════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'faturamento' && permissions.showCommission && (
          <div className="space-y-4">
            
            {/* SELETOR DE MÊS */}
            <div className="bg-white p-4 rounded-3xl border border-zinc-200 shadow-xs flex items-center justify-between gap-3">
              <div>
                <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block">Competência</span>
                <h3 className="text-sm font-black text-zinc-900">Extrato de Ganhos</h3>
              </div>

              <input
                type="month"
                value={selectedMonth}
                onChange={e => setSelectedMonth(e.target.value)}
                className="bg-zinc-50 text-zinc-900 text-xs font-black px-3 py-2 rounded-xl border border-zinc-200 focus:outline-none cursor-pointer"
              />
            </div>

            {/* CARDS DE INDICADORES PRINCIPAIS COM STATUS ANTI-FRAUDE */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {isOwner ? (
                <>
                  <div className="bg-linear-to-br from-emerald-50 to-white border border-emerald-300 p-4.5 rounded-3xl shadow-xs space-y-1">
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-800 flex items-center gap-1">
                      <span>👑</span>
                      <span>Faturamento Confirmado (Caixa)</span>
                    </span>
                    <h3 className="text-2xl font-black text-emerald-800">
                      {formatCurrencyBRL(financialSummary.paidGross)}
                    </h3>
                    <p className="text-[11px] text-emerald-700 font-bold">
                      {financialSummary.paidCount} recebimento(s) confirmados (100% Salão)
                    </p>
                  </div>

                  <div className="bg-linear-to-br from-amber-50 to-white border border-amber-300 p-4.5 rounded-3xl shadow-xs space-y-1">
                    <span className="text-[10px] font-black uppercase tracking-wider text-amber-900 flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5 text-amber-600" />
                      <span>Aguardando Baixa no Caixa</span>
                    </span>
                    <h3 className="text-2xl font-black text-amber-900">
                      {formatCurrencyBRL(financialSummary.pendingGross)}
                    </h3>
                    <p className="text-[11px] text-amber-800 font-bold">
                      {financialSummary.pendingCount} atendimento(s) a receber
                    </p>
                  </div>

                  <div className="bg-white border border-zinc-200 p-4.5 rounded-3xl shadow-xs space-y-1">
                    <span className="text-[10px] font-black uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                      <DollarSign className="w-3.5 h-3.5 text-zinc-400" />
                      <span>Total Produzido pelo Dono</span>
                    </span>
                    <h3 className="text-2xl font-black text-zinc-900">
                      {formatCurrencyBRL(financialSummary.totalGross)}
                    </h3>
                    <p className="text-[11px] text-zinc-500 font-bold">
                      100% Salão / Pró-labore
                    </p>
                  </div>
                </>
              ) : isClt ? (
                <>
                  <div className="bg-linear-to-br from-blue-50 to-white border border-blue-300 p-4.5 rounded-3xl shadow-xs space-y-1">
                    <span className="text-[10px] font-black uppercase tracking-wider text-blue-900 flex items-center gap-1">
                      <span>💼</span>
                      <span>Produção Confirmada (Caixa)</span>
                    </span>
                    <h3 className="text-2xl font-black text-blue-900">
                      {formatCurrencyBRL(financialSummary.paidGross)}
                    </h3>
                    <p className="text-[11px] text-blue-700 font-bold">
                      {financialSummary.paidCount} atendimento(s) pagos (Regime CLT)
                    </p>
                  </div>

                  <div className="bg-linear-to-br from-amber-50 to-white border border-amber-300 p-4.5 rounded-3xl shadow-xs space-y-1">
                    <span className="text-[10px] font-black uppercase tracking-wider text-amber-900 flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5 text-amber-600" />
                      <span>Aguardando Baixa no Caixa</span>
                    </span>
                    <h3 className="text-2xl font-black text-amber-900">
                      {formatCurrencyBRL(financialSummary.pendingGross)}
                    </h3>
                    <p className="text-[11px] text-amber-800 font-bold">
                      {financialSummary.pendingCount} atendimento(s) pendentes
                    </p>
                  </div>

                  <div className="bg-white border border-zinc-200 p-4.5 rounded-3xl shadow-xs space-y-1">
                    <span className="text-[10px] font-black uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                      <DollarSign className="w-3.5 h-3.5 text-zinc-400" />
                      <span>Total Produzido para a Empresa</span>
                    </span>
                    <h3 className="text-2xl font-black text-zinc-900">
                      {formatCurrencyBRL(financialSummary.totalGross)}
                    </h3>
                    <p className="text-[11px] text-zinc-500 font-bold">
                      Salário Fixo em Folha
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-linear-to-br from-emerald-50 to-white border border-emerald-300 p-4.5 rounded-3xl shadow-xs space-y-1">
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-800 flex items-center gap-1">
                      <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Comissão Liberada no Caixa</span>
                    </span>
                    <h3 className="text-2xl font-black text-emerald-800">
                      {formatCurrencyBRL(financialSummary.paidCommission)}
                    </h3>
                    <p className="text-[11px] text-emerald-700 font-bold">
                      {financialSummary.paidCount} recebimento(s) confirmados ({financialSummary.commissionPercent}%)
                    </p>
                  </div>

                  <div className="bg-linear-to-br from-amber-50 to-white border border-amber-300 p-4.5 rounded-3xl shadow-xs space-y-1">
                    <span className="text-[10px] font-black uppercase tracking-wider text-amber-900 flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5 text-amber-600" />
                      <span>Aguardando Baixa no Caixa</span>
                    </span>
                    <h3 className="text-2xl font-black text-amber-900">
                      {formatCurrencyBRL(financialSummary.pendingCommission)}
                    </h3>
                    <p className="text-[11px] text-amber-800 font-bold">
                      {financialSummary.pendingCount} serviço(s) executados
                    </p>
                  </div>

                  <div className="bg-white border border-zinc-200 p-4.5 rounded-3xl shadow-xs space-y-1">
                    <span className="text-[10px] font-black uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                      <DollarSign className="w-3.5 h-3.5 text-zinc-400" />
                      <span>Total Bruto Produzido</span>
                    </span>
                    <h3 className="text-2xl font-black text-zinc-900">
                      {formatCurrencyBRL(financialSummary.totalGross)}
                    </h3>
                    <p className="text-[11px] text-zinc-500 font-bold">
                      {financialSummary.completedCount} atendimentos no mês
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* LISTA DISCRIMINADA DE ATENDIMENTOS DO MÊS */}
            <div className="bg-white rounded-3xl border border-zinc-200 overflow-hidden shadow-xs">
              <div className="p-4 border-b border-zinc-100 flex items-center justify-between">
                <div>
                  <h4 className="text-xs font-black uppercase tracking-wider text-zinc-900">Detalhamento dos Atendimentos</h4>
                  <p className="text-[11px] text-zinc-500">Histórico com conferência de recebimento.</p>
                </div>
                <span className="text-xs font-black text-zinc-700 bg-zinc-100 px-2.5 py-1 rounded-xl">
                  {monthAppointments.length} no total
                </span>
              </div>

              {monthAppointments.length === 0 ? (
                <div className="p-8 text-center text-xs text-zinc-400 font-medium">
                  Nenhum atendimento registrado no mês de {selectedMonth}.
                </div>
              ) : (
                <div className="divide-y divide-zinc-100 max-h-96 overflow-y-auto">
                  {monthAppointments.map(appt => {
                    const commission = (appt.price * financialSummary.commissionPercent) / 100;
                    const dateFormatted = appt.scheduledDate ? appt.scheduledDate.split('-').reverse().slice(0, 2).join('/') : '--/--';
                    const isPaid = appt.paymentStatus === 'paid';
                    const isCompleted = appt.status === 'completed';

                    return (
                      <div key={appt.id} className="p-3.5 hover:bg-zinc-50 transition-colors flex items-center justify-between gap-3 text-xs">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-2xl bg-zinc-100 text-zinc-800 font-black text-[11px] flex items-center justify-center border border-zinc-200 shrink-0">
                            {dateFormatted}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-black text-zinc-900">{appt.clientName}</span>
                              <span className="text-[10px] text-zinc-400 font-mono font-bold">{appt.scheduledTime}</span>
                              {isPaid ? (
                                <span className="text-[9px] font-black bg-emerald-100 text-emerald-800 px-1.5 py-0.2 rounded-md border border-emerald-200">
                                  ✓ Caixa Confirmado
                                </span>
                              ) : isCompleted ? (
                                <span className="text-[9px] font-black bg-amber-100 text-amber-900 px-1.5 py-0.2 rounded-md border border-amber-300">
                                  ⏳ Aguardando Caixa
                                </span>
                              ) : (
                                <span className="text-[9px] font-bold bg-zinc-100 text-zinc-600 px-1.5 py-0.2 rounded-md">
                                  Agendado
                                </span>
                              )}
                            </div>
                            <span className="text-[11px] text-zinc-500 block font-medium">{appt.serviceName}</span>
                          </div>
                        </div>

                        <div className="text-right">
                          <span className="text-xs font-bold text-zinc-400 block">{formatCurrencyBRL(appt.price)}</span>
                          {isOwner ? (
                            <strong className={`text-xs font-black block ${isPaid ? 'text-emerald-800' : 'text-zinc-500'}`}>
                              {isPaid ? '100% Salão' : 'Aguardando'}
                            </strong>
                          ) : isClt ? (
                            <strong className="text-xs font-bold text-blue-800 block">
                              Salário CLT
                            </strong>
                          ) : (
                            <strong className={`text-xs font-black block ${isPaid ? 'text-emerald-700' : 'text-amber-800'}`}>
                              +{formatCurrencyBRL(commission)}
                            </strong>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* BOTÃO EXPORTAR / COMPARTILHAR EXTRATO */}
            <button
              type="button"
              onClick={() => {
                const text = isOwner
                  ? `👑 *Demonstrativo de Produção do Dono - ${data.staff.name}*\nEstabelecimento: ${data.businessName}\nCompetência: ${selectedMonth}\n\n🏛️ *Faturamento Confirmado no Caixa:* ${formatCurrencyBRL(financialSummary.paidGross)} (${financialSummary.paidCount} atendimentos pagos)\n⏳ *Aguardando Recebimento:* ${formatCurrencyBRL(financialSummary.pendingGross)} (${financialSummary.pendingCount} atendimentos)\n💰 *Total Produzido:* ${formatCurrencyBRL(financialSummary.totalGross)}\n\n💡 100% retido no caixa para despesas operacionais e pró-labore.\nEmitido via R-Max.`
                  : isClt
                  ? `💼 *Demonstrativo de Produção CLT - ${data.staff.name}*\nEstabelecimento: ${data.businessName}\nCompetência: ${selectedMonth}\n\n🏛️ *Atendimentos Pagos (Caixa):* ${formatCurrencyBRL(financialSummary.paidGross)} (${financialSummary.paidCount} atendimentos)\n⏳ *Aguardando Caixa:* ${formatCurrencyBRL(financialSummary.pendingGross)} (${financialSummary.pendingCount} atendimentos)\n💈 *Total Produzido para a Empresa:* ${formatCurrencyBRL(financialSummary.totalGross)}\n\nRegime de remuneração: Salário Fixo CLT.\nEmitido via R-Max.`
                  : `📊 *Demonstrativo de Ganhos - ${data.staff.name}*\nEstabelecimento: ${data.businessName}\nCompetência: ${selectedMonth}\n\n💎 *Comissão Confirmada no Caixa:* ${formatCurrencyBRL(financialSummary.paidCommission)} (${financialSummary.paidCount} atendimentos)\n⏳ *Aguardando Baixa no Caixa:* ${formatCurrencyBRL(financialSummary.pendingCommission)} (${financialSummary.pendingCount} atendimentos)\n💈 *Total Bruto Produzido:* ${formatCurrencyBRL(financialSummary.totalGross)}\n\nConferido via R-Max.`;
                navigator.clipboard.writeText(text);
                alert('Resumo do faturamento copiado com sucesso! Agora você pode colar no WhatsApp para conferência.');
              }}
              className="w-full py-3.5 bg-zinc-900 hover:bg-zinc-800 text-white font-black text-xs rounded-2xl flex items-center justify-center gap-2 cursor-pointer transition-all active:scale-98 shadow-sm"
            >
              <Copy className="w-4 h-4 text-emerald-400" />
              <span>Copiar Resumo do Mês para WhatsApp</span>
            </button>

          </div>
        )}

      </main>

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* MODAL: ALTERAR MEU PIN DE SEGURANÇA                                      */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {isChangingPinModalOpen && (
        <div className="fixed inset-0 z-50 bg-neutral-950/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
          <div className="bg-white border border-zinc-200 rounded-3xl max-w-sm w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-black">
                  <KeyRound className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-black text-zinc-900">Alterar PIN de Acesso</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsChangingPinModalOpen(false)}
                className="p-1 text-zinc-400 hover:text-zinc-700 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {pinChangeSuccess ? (
              <div className="p-6 text-center space-y-2">
                <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center">
                  <Check className="w-6 h-6" />
                </div>
                <h4 className="text-sm font-black text-zinc-900">PIN Alterado com Sucesso!</h4>
                <p className="text-xs text-zinc-500">Seu novo código já está ativo neste dispositivo.</p>
              </div>
            ) : (
              <form onSubmit={handleChangePinSubmit} className="space-y-3.5 text-xs">
                <p className="text-xs text-zinc-500">
                  Crie uma senha de 4 a 6 dígitos para proteger o acesso à sua agenda pessoal.
                </p>

                <div>
                  <label className="text-[11px] font-bold text-zinc-600 block mb-1">Novo PIN (4 a 6 números)</label>
                  <input
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    required
                    placeholder="Ex: 8754"
                    value={newPinValue}
                    onChange={e => setNewPinValue(e.target.value.replace(/\D/g, ''))}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 font-mono font-bold tracking-widest text-center text-base focus:border-emerald-600 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-zinc-600 block mb-1">Confirme o Novo PIN</label>
                  <input
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    required
                    placeholder="Repita o PIN"
                    value={confirmPinValue}
                    onChange={e => setConfirmPinValue(e.target.value.replace(/\D/g, ''))}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 font-mono font-bold tracking-widest text-center text-base focus:border-emerald-600 focus:outline-none"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-md cursor-pointer transition-all active:scale-98 mt-2"
                >
                  Salvar Novo PIN
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* MODAL: NOVO AGENDAMENTO MANUAL PELO PROFISSIONAL                         */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {isNewBookingModalOpen && (
        <div className="fixed inset-0 z-50 bg-neutral-950/60 backdrop-blur-xs flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white border border-zinc-200 rounded-t-3xl sm:rounded-3xl max-w-md w-full p-5 sm:p-6 max-h-[90vh] flex flex-col shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-black">
                  <Plus className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-black text-zinc-900">Agendar Novo Atendimento</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsNewBookingModalOpen(false)}
                className="p-1 text-zinc-400 hover:text-zinc-700 cursor-pointer rounded-lg hover:bg-zinc-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateAppointment} className="flex flex-col flex-1 overflow-hidden min-h-0">
              <div className="space-y-3.5 overflow-y-auto py-2 pr-1 flex-1 text-xs">
                <div>
                  <label className="text-[11px] font-bold text-zinc-600 block mb-1">Nome do Cliente *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Amanda Silva"
                    value={clientName}
                    onChange={e => setClientName(e.target.value)}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 placeholder-zinc-400 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 font-medium"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-zinc-600 block mb-1">WhatsApp / Telefone (Opcional)</label>
                  <input
                    type="tel"
                    placeholder="Ex: 11988887777"
                    value={clientPhone}
                    onChange={e => setClientPhone(e.target.value)}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 placeholder-zinc-400 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 font-medium"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-zinc-600 block mb-1">Serviço</label>
                  <select
                    value={selectedServiceId}
                    onChange={e => setSelectedServiceId(e.target.value)}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 cursor-pointer font-bold"
                  >
                    {allowedServices.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name} - {formatCurrencyBRL(s.price)} ({s.durationMinutes} min)
                      </option>
                    ))}
                    <option value="outro">Outro Serviço (Digitar manual)</option>
                  </select>
                </div>

                {selectedServiceId === 'outro' && (
                  <div>
                    <label className="text-[11px] font-bold text-zinc-600 block mb-1">Nome do Serviço Avulso</label>
                    <input
                      type="text"
                      required
                      placeholder="Ex: Cauterização Especial"
                      value={customServiceName}
                      onChange={e => setCustomServiceName(e.target.value)}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 focus:border-emerald-600 focus:outline-none font-medium"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] font-bold text-zinc-600 block mb-1">Data</label>
                    <input
                      type="date"
                      required
                      value={bookingDate}
                      onChange={e => setBookingDate(e.target.value)}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2.5 text-zinc-900 focus:border-emerald-600 focus:outline-none font-bold"
                    />
                  </div>

                  <div>
                    <label className="text-[11px] font-bold text-zinc-600 block mb-1">Horário de Início</label>
                    <select
                      value={bookingTime}
                      onChange={e => setBookingTime(e.target.value)}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 focus:border-emerald-600 focus:outline-none cursor-pointer font-bold"
                    >
                      {staffBookingSlots.length > 0 ? (
                        staffBookingSlots.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))
                      ) : (
                        TIME_SLOTS.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))
                      )}
                    </select>
                  </div>
                </div>

                {/* Duração com Caixinhas Rápidas */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[11px] font-bold text-zinc-600 block">Duração Estimada</label>
                    <span className="text-[10px] text-zinc-500 font-bold">
                      {bookingDuration || 0} min
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-1.5 mb-2">
                    {[15, 30, 45, 60].map((mins) => (
                      <button
                        key={mins}
                        type="button"
                        onClick={() => setBookingDuration(mins)}
                        className={`py-2 px-1 rounded-xl text-xs font-black transition-all border cursor-pointer ${
                          Number(bookingDuration) === mins
                            ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                            : 'bg-zinc-50 hover:bg-zinc-100 text-zinc-700 border-zinc-200'
                        }`}
                      >
                        {mins}m
                      </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="relative">
                      <input
                        type="number"
                        min={5}
                        step={5}
                        value={bookingDuration}
                        onFocus={e => e.currentTarget.select()}
                        onChange={e => {
                          const val = e.target.value.replace(/^0+/, '');
                          setBookingDuration(val === '' ? '' : parseInt(val, 10));
                        }}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2 text-zinc-900 focus:border-emerald-600 focus:outline-none font-bold text-xs"
                      />
                      <span className="absolute right-3 top-2 text-[10px] font-bold text-zinc-400">min</span>
                    </div>

                    <div className="relative">
                      <span className="absolute left-3 top-2 text-xs font-black text-emerald-800">R$</span>
                      <input
                        type="number"
                        step="0.5"
                        value={bookingPrice}
                        onFocus={e => e.currentTarget.select()}
                        onChange={e => {
                          const val = e.target.value.replace(/^0+(?=\d)/, '');
                          setBookingPrice(val === '' ? '' : val);
                        }}
                        className="w-full bg-emerald-50 border border-emerald-200 rounded-xl pl-8 pr-3 py-2 text-emerald-950 focus:border-emerald-600 focus:outline-none font-black text-xs"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-[11px] font-bold text-zinc-600 block mb-1">Observações / Ficha Técnica (Opcional)</label>
                  <textarea
                    rows={2}
                    placeholder="Ex: Cliente prefere formato amendoado / mechas tom mel..."
                    value={bookingNotes}
                    onChange={e => setBookingNotes(e.target.value)}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2 text-zinc-900 focus:border-emerald-600 focus:outline-none resize-none font-medium"
                  />
                </div>
              </div>

              <div className="pt-3 border-t border-zinc-100 shrink-0 bg-white">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-md cursor-pointer transition-all active:scale-98 disabled:opacity-50"
                >
                  {isSubmitting ? 'Agendando...' : 'Confirmar e Agendar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* MODAL: BLOQUEAR HORÁRIO / FOLGA                                          */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {isBlockModalOpen && (
        <div className="fixed inset-0 z-50 bg-neutral-950/60 backdrop-blur-xs flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white border border-zinc-200 rounded-t-3xl sm:rounded-3xl max-w-md w-full p-5 sm:p-6 max-h-[90vh] flex flex-col shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center font-black">
                  <Lock className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-black text-zinc-900">Bloquear Horário / Folga</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsBlockModalOpen(false)}
                className="p-1 text-zinc-400 hover:text-zinc-700 cursor-pointer rounded-lg hover:bg-zinc-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateBlock} className="flex flex-col flex-1 overflow-hidden min-h-0">
              <div className="space-y-3.5 overflow-y-auto py-2 pr-1 flex-1 text-xs">
                <div>
                  <label className="text-[11px] font-bold text-zinc-600 block mb-1.5">Selecione o Motivo Rápido</label>
                  <div className="grid grid-cols-2 gap-2">
                    {PRESET_BLOCK_REASONS.map(p => (
                      <button
                        type="button"
                        key={p.label}
                        onClick={() => {
                          setBlockReason(p.label);
                          setBlockDuration(p.defaultDuration);
                          setBlockIsAllDay(p.isAllDay);
                          if (!p.isAllDay) {
                            const startMin = timeStringToMinutes(blockTime);
                            setBlockEndTime(minutesToTimeString(startMin + p.defaultDuration));
                          }
                        }}
                        className={`p-2.5 rounded-xl border text-left flex items-center gap-2 transition-all cursor-pointer ${
                          blockReason === p.label 
                            ? 'border-amber-400 bg-amber-100 text-amber-950 font-black ring-2 ring-amber-400/20' 
                            : 'border-zinc-200 bg-zinc-50 text-zinc-700 hover:bg-zinc-100'
                        }`}
                      >
                        <span className="text-base">{p.icon}</span>
                        <span className="text-[11px] leading-tight">{p.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[11px] font-bold text-zinc-600 block mb-1">Data</label>
                  <input
                    type="date"
                    required
                    value={blockDate}
                    onChange={e => setBlockDate(e.target.value)}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2.5 text-zinc-900 focus:border-amber-500 focus:outline-none font-bold"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] font-bold text-zinc-600 block mb-1">Horário Inicial</label>
                    <select
                      disabled={blockIsAllDay}
                      value={blockTime}
                      onChange={e => {
                        const newStart = e.target.value;
                        setBlockTime(newStart);
                        const startMin = timeStringToMinutes(newStart);
                        const dur = calculatedBlockDuration > 0 ? calculatedBlockDuration : (Number(blockDuration) || 60);
                        setBlockEndTime(minutesToTimeString(startMin + dur));
                      }}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2.5 text-zinc-900 focus:border-amber-500 focus:outline-none disabled:opacity-40 cursor-pointer font-bold"
                    >
                      {staffBlockSlots.length > 0 ? (
                        staffBlockSlots.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))
                      ) : (
                        TIME_SLOTS.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="text-[11px] font-bold text-zinc-600 block mb-1">Horário Final (Término)</label>
                    <select
                      disabled={blockIsAllDay}
                      value={blockEndTime}
                      onChange={e => {
                        const newEnd = e.target.value;
                        setBlockEndTime(newEnd);
                        const diff = timeStringToMinutes(newEnd) - timeStringToMinutes(blockTime);
                        if (diff > 0) {
                          setBlockDuration(diff);
                        }
                      }}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2.5 text-zinc-900 focus:border-amber-500 focus:outline-none disabled:opacity-40 cursor-pointer font-bold"
                    >
                      {availableEndSlots.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Resumo visual do período do bloqueio */}
                <div className="flex items-center justify-between px-3 py-2 bg-amber-50/80 border border-amber-200/80 rounded-xl text-amber-950 text-[11px]">
                  <span className="text-zinc-600 font-medium">Período de bloqueio:</span>
                  <span className="font-bold">
                    {blockIsAllDay ? '🌴 Dia inteiro (Folga)' : `⏱️ ${blockTime} às ${blockEndTime} (${formattedBlockDurationText})`}
                  </span>
                </div>

                <div className="flex items-center gap-2 p-3 bg-zinc-50 rounded-xl border border-zinc-200">
                  <input
                    type="checkbox"
                    id="allDayCheck"
                    checked={blockIsAllDay}
                    onChange={e => setBlockIsAllDay(e.target.checked)}
                    className="rounded text-amber-600 focus:ring-0 cursor-pointer"
                  />
                  <label htmlFor="allDayCheck" className="text-xs text-zinc-700 cursor-pointer font-medium">
                    Bloquear <strong>o dia inteiro</strong> (Folga / Indisponível)
                  </label>
                </div>
              </div>

              <div className="pt-3 border-t border-zinc-100 shrink-0 bg-white">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 bg-amber-600 hover:bg-amber-700 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-md cursor-pointer transition-all active:scale-98 disabled:opacity-50"
                >
                  {isSubmitting ? 'Bloqueando...' : 'Confirmar Bloqueio'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RODAPÉ DISCRETO COM STATUS DE SEGURANÇA */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-zinc-200/90 py-2.5 px-4 text-center z-20 shadow-xs flex items-center justify-between max-w-2xl mx-auto">
        <p className="text-[11px] text-zinc-500 flex items-center gap-1 font-medium">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
          <span>Acesso Protegido por PIN</span>
        </p>
        <button
          type="button"
          onClick={handleLockDevice}
          className="text-[11px] text-zinc-400 hover:text-rose-600 font-bold transition-colors cursor-pointer"
        >
          Bloquear Aparelho
        </button>
      </footer>
    </div>
  );
}
